import { Fragment, useCallback, useRef, useState } from 'react';
import { withPlural } from '@/lib/format';
import { Button } from './Button';
import { useDismiss } from './useDismiss';

/**
 * Период в ряду фильтров — календарь кита `.calwrap` (DateRangePicker):
 * пресеты слева, два месяца, внизу число дней и «Применить». Выбор не
 * применяется на лету — иначе первый клик перезагрузил бы список
 * на середине выбора. Поповер, поэтому только вне модального окна.
 *
 * Кит 2.15: десять календарных пресетов (текущие — до сегодня включительно,
 * прошлые — целиком), пресет подсвечен, если даты с ним совпали; справа —
 * месяц, где кончается период; «Применить» — только с концом периода.
 *
 * Наружу и внутрь — ISO `ГГГГ-ММ-ДД`.
 */

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];
const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
/** Две группы через `.psep`: текущие периоды, затем прошлые */
const PRESETS = [
  { key: 'd', label: 'Сегодня' },
  { key: 'cw', label: 'Текущая неделя' },
  { key: 'cm', label: 'Текущий месяц' },
  { key: 'cq', label: 'Текущий квартал' },
  { key: 'cy', label: 'Текущий год' },
  { key: 'y', label: 'Вчера' },
  { key: 'pw', label: 'Прошлая неделя' },
  { key: 'pm', label: 'Прошлый месяц' },
  { key: 'pq', label: 'Прошлый квартал' },
  { key: 'py', label: 'Прошлый год' },
] as const;
type PresetKey = (typeof PRESETS)[number]['key'];

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parse = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const ru = (s: string) => s.split('-').reverse().join('.');
const short = (s: string) => ru(s).slice(0, 5);
const daysBetween = (a: string, b: string) => Math.round((parse(b).getTime() - parse(a).getTime()) / 86_400_000);

/**
 * Границы пресета на сегодня. Неделя — с понедельника, квартал и год
 * календарные. Пример на 02.11.2026: «Текущий месяц» — 01.11–02.11,
 * «Прошлый квартал» — 01.07–30.09.
 */
export function presetRange(key: PresetKey, now = new Date()): [string, string] {
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const day = (yy: number, mm: number, dd: number) => iso(new Date(yy, mm, dd));
  const monday = d - ((now.getDay() + 6) % 7);
  const q = m - (m % 3);
  switch (key) {
    case 'd':
      return [day(y, m, d), day(y, m, d)];
    case 'y':
      return [day(y, m, d - 1), day(y, m, d - 1)];
    case 'cw':
      return [day(y, m, monday), day(y, m, d)];
    case 'pw':
      return [day(y, m, monday - 7), day(y, m, monday - 1)];
    case 'cm':
      return [day(y, m, 1), day(y, m, d)];
    case 'pm':
      return [day(y, m - 1, 1), day(y, m, 0)];
    case 'cq':
      return [day(y, q, 1), day(y, m, d)];
    case 'pq':
      return [day(y, q - 3, 1), day(y, q, 0)];
    case 'cy':
      return [day(y, 0, 1), day(y, m, d)];
    case 'py':
      return [day(y - 1, 0, 1), day(y - 1, 11, 31)];
  }
}

/** Пресет, с которым совпали выбранные даты — даже выбранные руками */
function matchedPreset(start: string | null, end: string | null): PresetKey | null {
  if (!start || !end) return null;
  return PRESETS.find((p) => {
    const [a, b] = presetRange(p.key);
    return a === start && b === end;
  })?.key ?? null;
}

/** Первое число месяца, где кончается период; без периода — текущего */
const anchorOf = (from: string | null, to: string | null) => {
  const end = to ?? from;
  const d = end ? parse(end) : new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

/** «всё время», «15.10.2026», «01.10 — 15.10.2026» */
export function periodLabel(from: string | null, to: string | null): string {
  if (!from) return 'всё время';
  const end = to ?? from;
  return from === end ? ru(from) : `${short(from)} — ${ru(end)}`;
}

function Month({
  year,
  month,
  start,
  end,
  onPick,
  prev,
  next,
  edge,
}: {
  year: number;
  month: number;
  start: string | null;
  end: string | null;
  onPick: (d: string) => void;
  prev: () => void;
  next: () => void;
  /**
   * Край пары: у левого месяца на десктопе видна только «назад», у правого —
   * только «вперёд». На телефоне кит показывает один месяц и обе стрелки,
   * поэтому обработчики есть у обоих (кит 2.15).
   */
  edge: 'left' | 'right';
}) {
  const first = new Date(year, month, 1);
  const shift = (first.getDay() + 6) % 7;
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(year, month, 1 - shift + i);
    if (i >= 35 && d.getMonth() !== month) break;
    cells.push(d);
  }
  const today = iso(new Date());
  return (
    <div className="cal">
      <div className="calh">
        <button type="button" className={edge === 'left' ? '' : 'gh'} onClick={prev}>
          ‹
        </button>
        <div className="m">
          {MONTHS[month]} {year}
        </div>
        <button type="button" className={edge === 'right' ? '' : 'gh'} onClick={next}>
          ›
        </button>
      </div>
      <div className="cg">
        {WEEKDAYS.map((w) => (
          <div className="wd" key={w}>
            {w}
          </div>
        ))}
        {cells.map((d) => {
          const x = iso(d);
          const c = ['d'];
          if (d.getMonth() !== month) c.push('out');
          if (start && end && x > start && x < end) c.push('in');
          if (x === start) c.push(end && end !== start ? 's' : 'one');
          else if (x === end) c.push('e');
          // красная цифра «сегодня» на выбранном дне снимается: спорила бы с выбором
          if (x === today && x !== start && x !== end) c.push('today');
          return (
            <div key={x} className={c.join(' ')} onClick={() => onPick(x)}>
              {d.getDate()}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DateRangePicker({
  label,
  from,
  to,
  onChange,
  alignRight,
}: {
  label: string;
  from: string | null;
  to: string | null;
  onChange: (from: string | null, to: string | null) => void;
  alignRight?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState<string | null>(null);
  const [end, setEnd] = useState<string | null>(null);
  const [anchor, setAnchor] = useState(() => anchorOf(from, to));
  const [shift, setShift] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, wrap);

  const toggle = () => {
    if (!open) {
      setStart(from);
      setEnd(to);
      setAnchor(anchorOf(from, to));
      setShift(0);
    }
    setOpen((v) => !v);
  };
  const pick = (d: string) => {
    if (!start || end) {
      setStart(d);
      setEnd(null);
    } else if (d < start) setStart(d);
    else setEnd(d);
  };

  const right = new Date(anchor.getFullYear(), anchor.getMonth() + shift, 1);
  const left = new Date(right.getFullYear(), right.getMonth() - 1, 1);
  const n = start ? daysBetween(start, end ?? start) + 1 : 0;
  const preset = matchedPreset(start, end);
  const back = () => setShift((s) => s - 1);
  const forward = () => setShift((s) => s + 1);

  return (
    <div className="wrapctl" ref={wrap}>
      <div className={from ? 'ctl act' : 'ctl'} onClick={toggle}>
        <svg>
          <use href="#i-cal" />
        </svg>
        {label}: {periodLabel(from, to)}
        {from ? (
          <span
            className="x"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null, null);
              setOpen(false);
            }}
          >
            ✕
          </span>
        ) : null}
      </div>
      {open ? (
        <div className={alignRight ? 'calwrap rt' : 'calwrap'}>
          <div className="presets">
            {PRESETS.map((p) => (
              <Fragment key={p.key}>
                {p.key === 'y' ? <i className="psep" /> : null}
                <span
                  className={preset === p.key ? 'on' : ''}
                  onClick={() => {
                    const [a, b] = presetRange(p.key);
                    setStart(a);
                    setEnd(b);
                    setAnchor(anchorOf(a, b));
                    setShift(0);
                  }}
                >
                  {p.label}
                </span>
              </Fragment>
            ))}
          </div>
          <div>
            <div className="cals">
              <Month
                year={left.getFullYear()}
                month={left.getMonth()}
                start={start}
                end={end}
                onPick={pick}
                prev={back}
                next={forward}
                edge="left"
              />
              <Month
                year={right.getFullYear()}
                month={right.getMonth()}
                start={start}
                end={end}
                onPick={pick}
                prev={back}
                next={forward}
                edge="right"
              />
            </div>
            <div className="calf">
              <span className="dim">
                {!start
                  ? 'Выберите начало и конец периода'
                  : !end
                    ? 'Выберите конец периода'
                    : `${withPlural(n, 'день', 'дня', 'дней')} · ${periodLabel(start, end)}`}
              </span>
              <span className="sp" />
              <Button
                small
                onClick={() => {
                  onChange(null, null);
                  setOpen(false);
                }}
              >
                Сбросить
              </Button>
              <Button
                small
                tone="primary"
                disabled={!start || !end}
                onClick={() => {
                  onChange(start, end);
                  setOpen(false);
                }}
              >
                Применить
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
